import { memo, useEffect, useRef, useState } from 'react';
import { ListMusic, Music2, Pause, Play, SkipBack, SkipForward, Volume2, X } from 'lucide-react';
import { storageGet, storageSet } from '../browserCompat';

const tracks = [
  { title: 'Sau Tarah', artist: 'Dishoom', src: '/music/sau-tarah.mp3' },
  { title: 'Character Dheela', artist: 'Pritam · Neeraj Shridhar · Amrita Kak', src: '/music/character-dheela.mp3' },
  { title: 'Badtameez Dil', artist: 'Pritam · Benny Dayal · Shefali Alvarez', src: '/music/badtameez-dil.mp3' },
  { title: 'Pink Lips', artist: 'Meet Bros Anjjan · Khushboo Grewal', src: '/music/pink-lips.mp3' },
  { title: 'Bang Bang', artist: 'Vishal & Sheykhar', src: '/music/bang-bang.mp3' },
  { title: 'Brown Rang', artist: 'Yo Yo Honey Singh', src: '/music/brown-rang.mp3' },
  { title: 'Attention', artist: 'Charlie Puth', src: '/music/attention.mp3' },
  { title: 'Old Town Road', artist: 'Lil Nas X · Billy Ray Cyrus', src: '/music/old-town-road.mp3' },
  { title: 'Thick Of It', artist: 'KSI · Trippie Redd', src: '/music/thick-of-it.mp3' },
  { title: 'Last Friday Night (T.G.I.F.)', artist: 'Katy Perry', src: '/music/last-friday-night.mp3' },
  { title: 'On The Floor', artist: 'Jennifer Lopez · Pitbull', src: '/music/on-the-floor.mp3' },
  { title: 'Dracula — JENNIE Remix', artist: 'Tame Impala · JENNIE', src: '/music/dracula-jennie-remix.mp3' },
] as const;

function clock(value: number) {
  if (!Number.isFinite(value)) return '0:00';
  const minutes = Math.floor(value / 60);
  return `${minutes}:${Math.floor(value % 60).toString().padStart(2, '0')}`;
}

export const MusicPlayer = memo(function MusicPlayer() {
  const audio = useRef<HTMLAudioElement>(null);
  const [trackIndex, setTrackIndex] = useState(() => Number(storageGet('dismun.eb.music.track') ?? 0) || 0);
  const [playing, setPlaying] = useState(false);
  const [playlistOpen, setPlaylistOpen] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(() => {
    const stored = Number(storageGet('dismun.eb.music.volume'));
    return Number.isFinite(stored) && stored >= 0 && stored <= 1 ? stored : .72;
  });
  const track = tracks[Math.min(trackIndex, tracks.length - 1)];

  useEffect(() => {
    storageSet('dismun.eb.music.track', String(trackIndex));
    const player = audio.current;
    if (!player) return;
    player.load();
    setTime(0);
    setDuration(0);
    if (playing) void player.play().catch(() => setPlaying(false));
  }, [trackIndex]);

  useEffect(() => {
    if (!audio.current) return;
    audio.current.volume = volume;
    storageSet('dismun.eb.music.volume', String(volume));
  }, [volume]);

  const move = (direction: -1 | 1) => {
    setTrackIndex((current) => (current + direction + tracks.length) % tracks.length);
    setPlaying(true);
  };

  const toggle = async () => {
    const player = audio.current;
    if (!player) return;
    if (player.paused) {
      try { await player.play(); setPlaying(true); } catch { setPlaying(false); }
    } else {
      player.pause(); setPlaying(false);
    }
  };

  return <aside className={`music-player ${playlistOpen ? 'playlist-open' : ''}`} aria-label="DISMUN music player">
    <audio ref={audio} src={track.src} preload="metadata" onTimeUpdate={(event) => setTime(event.currentTarget.currentTime)} onLoadedMetadata={(event) => setDuration(event.currentTarget.duration)} onEnded={() => move(1)} onPause={() => setPlaying(false)} onPlay={() => setPlaying(true)} />
    {playlistOpen && <div className="music-playlist">
      <header><div><span>DISMUN PLAYLIST</span><strong>{tracks.length} tracks</strong></div><button type="button" aria-label="Close playlist" onClick={() => setPlaylistOpen(false)}><X /></button></header>
      <div className="music-track-list">
        {tracks.map((item, index) => <button type="button" className={index === trackIndex ? 'active' : ''} key={item.src} onClick={() => { setTrackIndex(index); setPlaying(true); }}>
          <span>{(index + 1).toString().padStart(2, '0')}</span><div><strong>{item.title}</strong><small>{item.artist}</small></div>{index === trackIndex && <i>{playing ? 'PLAYING' : 'SELECTED'}</i>}
        </button>)}
      </div>
    </div>}
    <div className="music-console">
      <button type="button" className="music-cover" aria-label="Open playlist" onClick={() => setPlaylistOpen((open) => !open)}><Music2 /></button>
      <div className="music-now"><span>NOW PLAYING</span><strong>{track.title}</strong><small>{track.artist}</small></div>
      <div className="music-transport">
        <button type="button" aria-label="Previous track" onClick={() => move(-1)}><SkipBack /></button>
        <button type="button" className="music-play" aria-label={playing ? 'Pause' : 'Play'} onClick={() => void toggle()}>{playing ? <Pause /> : <Play />}</button>
        <button type="button" aria-label="Next track" onClick={() => move(1)}><SkipForward /></button>
      </div>
      <button type="button" className="music-list-button" aria-label="Show playlist" onClick={() => setPlaylistOpen((open) => !open)}><ListMusic /></button>
      <div className="music-progress">
        <span>{clock(time)}</span><input aria-label="Song position" type="range" min="0" max={duration || 0} step=".1" value={Math.min(time, duration || 0)} onChange={(event) => { const next = Number(event.target.value); if (audio.current) audio.current.currentTime = next; setTime(next); }} /><span>{clock(duration)}</span>
      </div>
      <div className="music-volume"><Volume2 /><input aria-label="Music volume" type="range" min="0" max="1" step=".02" value={volume} onChange={(event) => setVolume(Number(event.target.value))} /></div>
    </div>
  </aside>;
});
